import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import {
  deleteOrgCoverApi,
  getOrganizationApi,
  updateOrganizationApi,
  uploadOrgCoverApi,
  uploadOrgLogoApi,
} from '@/lib/api/organizationsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import {
  citycodeFieldOptional,
  emailFieldOptional,
  entityNameField,
  freeTextFieldOptional,
  phoneFieldOptional,
  placeNameFieldOptional,
  postcodeFieldOptional,
  siretFieldOptional,
  urlFieldOptional,
} from '@/lib/validation/fields';
import { useAuthStore } from '@/stores/auth-store';
import { ApiError } from '@api-client/client';
import { getNameInitials } from '@shared';
import type { AddressSuggestion, Organization } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Camera,
  Globe,
  ImageIcon,
  Info,
  Key,
  Mail,
  Map,
  MapPin,
  Phone,
  Save,
  Trash2,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { AddressAutocomplete } from '../../components/geo/AddressAutocomplete';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LeafletMap } from '../../components/map/mapLeaflet';
import PageHeader from '../../components/ui/PageHeader';

// ─── Styles ───────────────────────────────────────────────────
const inputBase = 'w-full px-4 py-3 border rounded-lg focus:outline-none text-slate-900 bg-white transition-colors';
const inputClass = `${inputBase} border-slate-200 focus:border-blue-500`;
const inputErrorClass = `${inputBase} border-red-300 focus:border-red-500 bg-red-50/30`;
const labelClass = 'block text-sm font-semibold text-slate-900 mb-2';
const optionalBadge = 'ml-1.5 text-xs font-normal text-slate-400';
const errorClass = 'mt-1 text-xs text-red-600';

// ─── Helpers ──────────────────────────────────────────────────
const AVATAR_BG = ['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-cyan-500', 'bg-emerald-500'];

// ─── Zod schema ───────────────────────────────────────────────
const OrgSettingsSchema = z.object({
  name: entityNameField("Nom de l'organisation", { min: 2, max: 100 }),
  description: freeTextFieldOptional(2000),
  address: freeTextFieldOptional(255),
  city: placeNameFieldOptional(),
  department: placeNameFieldOptional(),
  region: placeNameFieldOptional(),
  postcode: postcodeFieldOptional,
  citycode: citycodeFieldOptional,
  email: emailFieldOptional,
  phone: phoneFieldOptional,
  website: urlFieldOptional,
  siret: siretFieldOptional,
});

type OrgForm = z.infer<typeof OrgSettingsSchema> & {
  latitude: number | null;
  longitude: number | null;
};

type FormErrors = Partial<Record<keyof OrgForm, string>>;

const readonlyClass =
  'w-full px-4 py-3 border border-slate-100 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed select-text';

// ─── EditForm ─────────────────────────────────────────────────
function EditForm({ org }: { org: Organization }) {
  const queryClient = useQueryClient();
  const { hash } = useLocation();
  const siretInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (hash === '#organization-siret') siretInputRef.current?.focus();
  }, [hash]);
  const user = useAuthStore(s => s.user);
  /* const userRef = useAuthStore(s => s.user);

  const [copied, setCopied] = useState(false);
  function copyReferralCode() {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }*/

  const logoFile = org.files?.find(f => f.usage === 'ORG_LOGO') ?? null;
  const coverFile = org.files?.find(f => f.usage === 'ORG_COVER') ?? null;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(logoFile ? getFileUrl(logoFile) : null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(coverFile ? getFileUrl(coverFile) : null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  const [coverPendingDelete, setCoverPendingDelete] = useState(false);

  const [form, setForm] = useState<OrgForm>({
    name: org.name ?? '',
    description: org.description ?? '',
    address: org.address ?? '',
    city: org.city ?? '',
    department: org.department ?? '',
    region: org.region ?? '',
    postcode: org.postcode ?? '',
    citycode: org.citycode ?? '',
    email: org.email ?? '',
    phone: org.phone ?? '',
    website: org.website ?? '',
    siret: org.siret ?? '',
    latitude: org.latitude ?? null,
    longitude: org.longitude ?? null,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  function setField<K extends keyof OrgForm>(key: K, value: OrgForm[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function validateField(key: keyof z.input<typeof OrgSettingsSchema>, value: string) {
    const partial = OrgSettingsSchema.pick({ [key]: true } as Record<typeof key, true>);
    const result = partial.safeParse({ [key]: value });
    setErrors(prev => ({
      ...prev,
      [key]: result.success ? undefined : result.error.issues[0]?.message,
    }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedCoverFile(file);
    setCoverPreviewUrl(URL.createObjectURL(file));
    setCoverPendingDelete(false);
  }

  function handleAddressSelect(suggestion: AddressSuggestion) {
    setForm(prev => ({
      ...prev,
      address: suggestion.label,
      city: suggestion.city ?? prev.city,
      postcode: suggestion.postcode ?? prev.postcode,
      citycode: suggestion.citycode ?? prev.citycode,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    }));
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const result = OrgSettingsSchema.safeParse(form);
      if (!result.success) {
        const fieldErrors = result.error.flatten().fieldErrors;
        setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([k, v]) => [k, v?.[0]])));
        toast.error('Veuillez corriger les erreurs du formulaire');
        throw new Error('Formulaire invalide');
      }

      setErrors({});

      if (selectedFile) {
        await uploadOrgLogoApi(org.id, selectedFile);
        setSelectedFile(null);
      }

      if (coverPendingDelete && coverFile) {
        await deleteOrgCoverApi(coverFile.id);
        setCoverPendingDelete(false);
      } else if (selectedCoverFile) {
        await uploadOrgCoverApi(org.id, selectedCoverFile);
        setSelectedCoverFile(null);
      }

      // Convert empty strings to undefined for optional fields
      const emptyToUndefined = (v: string | undefined) => (v === '' ? undefined : v);

      const payload = {
        ...(form.name && { name: form.name }),
        description: emptyToUndefined(form.description),
        address: emptyToUndefined(form.address),
        ...(form.city && { city: form.city }),
        ...(form.department && { department: form.department }),
        ...(form.region && { region: form.region }),
        postcode: emptyToUndefined(form.postcode),
        citycode: emptyToUndefined(form.citycode),
        ...(form.latitude != null && { latitude: form.latitude }),
        ...(form.longitude != null && { longitude: form.longitude }),
        email: emptyToUndefined(form.email),
        phone: emptyToUndefined(form.phone),
        website: emptyToUndefined(form.website),
        // Preserve an existing standalone SIREN when SIRET has never been supplied.
        ...(result.data.siret !== (org.siret ?? '') && { siret: result.data.siret || null }),
      };

      return updateOrganizationApi(org.id, payload);
    },
    onSuccess: updatedOrg => {
      setForm(prev => ({ ...prev, siret: updatedOrg.siret ?? '' }));
      toast.success('Organisation mise à jour avec succès');
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(org.id) });
    },
    onError: (err: Error) => {
      if (err.message !== 'Formulaire invalide' && !(err instanceof ApiError && err.toastShown)) {
        toast.error(err.message || 'Erreur lors de la sauvegarde');
      }
    },
  });

  // ─── Unsaved-changes guard ─────────────────────────────
  const initialFormState = useMemo<OrgForm>(
    () => ({
      name: org.name ?? '',
      description: org.description ?? '',
      address: org.address ?? '',
      city: org.city ?? '',
      department: org.department ?? '',
      region: org.region ?? '',
      postcode: org.postcode ?? '',
      citycode: org.citycode ?? '',
      email: org.email ?? '',
      phone: org.phone ?? '',
      website: org.website ?? '',
      siret: org.siret ?? '',
      latitude: org.latitude ?? null,
      longitude: org.longitude ?? null,
    }),
    [org],
  );

  const isDirty = useMemo(() => {
    return (
      (Object.keys(initialFormState) as (keyof OrgForm)[]).some(k => form[k] !== initialFormState[k]) ||
      !!selectedFile ||
      !!selectedCoverFile ||
      coverPendingDelete
    );
  }, [form, initialFormState, selectedFile, selectedCoverFile, coverPendingDelete]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const avatarBg = AVATAR_BG[org.id % AVATAR_BG.length];
  const hasCoords = form.latitude != null && form.longitude != null;

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
      <PageHeader
        title='Paramètres'
        subtitle='Configuration de votre espace organisateur'
        actions={
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !isDirty}
            className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm'
          >
            <Save className='w-4 h-4' />
            {saveMutation.isPending ? 'Enregistrement…' : 'Sauvegarder'}
          </button>
        }
      />

      {isDirty && (
        <div className='mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800'>
          <Info className='w-4 h-4 shrink-0' />
          Vous avez des modifications non sauvegardées
        </div>
      )}

      <div className='space-y-6 sm:space-y-8'>
        {/* Section 1 — Logo */}
        <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
          <h2 className='text-base font-bold text-slate-900 mb-6 flex items-center gap-2'>
            <Building2 className='w-5 h-5 text-blue-600' />
            Logo de l&apos;organisation
          </h2>

          <div className='flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6'>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={org.name}
                className='w-24 h-24 rounded-2xl object-cover shadow border border-slate-200 shrink-0'
              />
            ) : (
              <div
                className={`w-24 h-24 ${avatarBg} rounded-2xl flex items-center justify-center text-white font-extrabold text-3xl shadow shrink-0`}
              >
                {getNameInitials(org.name)}
              </div>
            )}

            <div className='min-w-0'>
              <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:border-blue-300 transition text-sm font-semibold text-slate-700'
              >
                <Camera className='w-4 h-4' />
                {logoFile || previewUrl ? 'Changer le logo' : 'Ajouter un logo'}
              </button>

              <input
                ref={fileInputRef}
                type='file'
                accept='image/jpeg,image/png,image/gif,image/webp'
                onChange={handleFileChange}
                className='hidden'
              />

              <p className='text-xs text-slate-500 mt-2'>JPG, PNG ou WebP — max 10 Mo.</p>
              <p className='text-xs text-slate-500'>Recommandé : 300×300 px (carré).</p>
            </div>
          </div>
        </div>

        {/* Section 1b — Bannière */}
        <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
          <h2 className='text-base font-bold text-slate-900 mb-6 flex items-center gap-2'>
            <ImageIcon className='w-5 h-5 text-indigo-600' />
            Bannière de couverture
          </h2>

          <div className='space-y-4'>
            {coverPreviewUrl && !coverPendingDelete ? (
              <div className='relative rounded-xl overflow-hidden border border-slate-200'>
                <img src={coverPreviewUrl} alt='Bannière' className='w-full h-40 object-cover object-top' />
              </div>
            ) : (
              <div className='w-full h-40 bg-linear-to-br from-purple-900 via-indigo-600 to-blue-800 rounded-xl flex items-center justify-center'>
                <p className='text-white/60 text-sm font-medium px-4 text-center'>
                  {coverPendingDelete ? 'Bannière supprimée (sauvegarder pour confirmer)' : 'Aucune bannière'}
                </p>
              </div>
            )}

            <div className='flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4'>
              <button
                type='button'
                onClick={() => {
                  setCoverPendingDelete(false);
                  coverInputRef.current?.click();
                }}
                className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:border-indigo-300 transition text-sm font-semibold text-slate-700'
              >
                <Camera className='w-4 h-4' />
                {(coverFile || coverPreviewUrl) && !coverPendingDelete ? 'Changer la bannière' : 'Ajouter une bannière'}
              </button>

              {(coverFile || (coverPreviewUrl && !coverPendingDelete)) && (
                <button
                  type='button'
                  onClick={() => {
                    setCoverPendingDelete(true);
                    setSelectedCoverFile(null);
                    setCoverPreviewUrl(null);
                  }}
                  className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-red-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition text-sm font-semibold text-red-600'
                >
                  <Trash2 className='w-4 h-4' />
                  Supprimer
                </button>
              )}

              <input
                ref={coverInputRef}
                type='file'
                accept='image/jpeg,image/png,image/gif,image/webp'
                onChange={handleCoverChange}
                className='hidden'
              />

              <p className='text-xs text-slate-500 leading-relaxed sm:ml-auto'>
                Recommandé : 1920×480 px (paysage 4:1). Max 10 Mo.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2 — Informations générales */}
        <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
          <h2 className='text-base font-bold text-slate-900 mb-6 flex items-center gap-2'>
            <Info className='w-5 h-5 text-emerald-600' />
            Informations générales
          </h2>

          <div className='space-y-5'>
            <div>
              <label className={labelClass}>
                Nom de l&apos;organisation <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                onBlur={e => validateField('name', e.target.value)}
                className={errors.name ? inputErrorClass : inputClass}
              />
              {errors.name && <p className={errorClass}>{errors.name}</p>}
            </div>

            <div id='organization-siret'>
              <label htmlFor='organization-siret-input' className={labelClass}>
                Numéro SIRET <span className={optionalBadge}>14 chiffres</span>
              </label>
              <input
                id='organization-siret-input'
                ref={siretInputRef}
                type='text'
                inputMode='numeric'
                value={form.siret}
                onChange={e => {
                  setField('siret', e.target.value);
                  validateField('siret', e.target.value);
                }}
                onBlur={e => validateField('siret', e.target.value)}
                placeholder='123 456 789 00012'
                aria-invalid={!!errors.siret}
                aria-describedby={
                  errors.siret ? 'organization-siret-error organization-siret-help' : 'organization-siret-help'
                }
                className={errors.siret ? inputErrorClass : inputClass}
              />
              {errors.siret && (
                <p id='organization-siret-error' role='alert' className={errorClass}>
                  {errors.siret}
                </p>
              )}
              <p id='organization-siret-help' className='mt-2 text-xs text-slate-500'>
                Le SIREN nécessaire à la connexion Stripe sera déduit automatiquement des 9 premiers chiffres.
                {org.siren && ` SIREN enregistré : ${org.siren}.`}
              </p>
            </div>

            <div>
              <label className={labelClass}>
                Description <span className={optionalBadge}>optionnel</span>
              </label>
              <textarea
                rows={5}
                value={form.description}
                onChange={e => setField('description', e.target.value)}
                placeholder='Décrivez votre organisation, votre mission, vos activités…'
                className={inputClass}
              />
              {errors.description && <p className={errorClass}>{errors.description}</p>}
            </div>
          </div>
        </div>

        {/* Section 3 — Localisation */}
        <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
          <h2 className='text-base font-bold text-slate-900 mb-6 flex items-center gap-2'>
            <MapPin className='w-5 h-5 text-purple-600' />
            Localisation
          </h2>

          <div className='space-y-5'>
            <div>
              <label className={labelClass}>Adresse</label>
              <AddressAutocomplete
                value={form.address ?? ''}
                onChange={value => setField('address', value)}
                onSelect={handleAddressSelect}
                placeholder='Rechercher une adresse…'
              />
              {errors.address && <p className={errorClass}>{errors.address}</p>}
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5'>
              <div>
                <label className={labelClass}>Ville</label>
                <input
                  type='text'
                  value={form.city}
                  onChange={e => setField('city', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Code postal</label>
                <input
                  type='text'
                  value={form.postcode}
                  onChange={e => setField('postcode', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Département</label>
                <input
                  type='text'
                  value={form.department}
                  onChange={e => setField('department', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Région</label>
              <input
                type='text'
                value={form.region}
                onChange={e => setField('region', e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Aperçu carte</label>
              {hasCoords ? (
                <div className='h-56 rounded-xl overflow-hidden border border-slate-200'>
                  <LeafletMap
                    key={`${form.latitude ?? 0}-${form.longitude ?? 0}`}
                    lat={form.latitude ?? undefined}
                    lng={form.longitude ?? undefined}
                  />
                </div>
              ) : (
                <div className='h-56 bg-slate-50 rounded-xl flex items-center justify-center border border-slate-200'>
                  <div className='text-center px-6'>
                    <Map className='w-10 h-10 text-slate-300 mx-auto mb-2' />
                    <p className='text-sm text-slate-400'>
                      Sélectionnez une adresse via l&apos;autocomplétion pour afficher la carte
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 4 — Contact */}
        <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
          <h2 className='text-base font-bold text-slate-900 mb-6 flex items-center gap-2'>
            <Phone className='w-5 h-5 text-amber-600' />
            Contact
          </h2>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5'>
            <div>
              <label className={labelClass}>
                Email de contact <span className={optionalBadge}>optionnel</span>
              </label>
              <input
                type='email'
                value={form.email}
                onChange={e => setField('email', e.target.value)}
                onBlur={e => validateField('email', e.target.value)}
                placeholder='contact@mon-organisation.fr'
                className={errors.email ? inputErrorClass : inputClass}
              />
              {errors.email && <p className={errorClass}>{errors.email}</p>}
            </div>

            <div>
              <label className={labelClass}>
                Téléphone <span className={optionalBadge}>optionnel</span>
              </label>
              <input
                type='tel'
                value={form.phone}
                onChange={e => setField('phone', e.target.value)}
                onBlur={e => validateField('phone', e.target.value)}
                placeholder='04 78 12 34 56'
                className={errors.phone ? inputErrorClass : inputClass}
              />
              {errors.phone && <p className={errorClass}>{errors.phone}</p>}
            </div>

            <div className='md:col-span-2'>
              <label className={labelClass}>
                <span className='flex items-center gap-1.5'>
                  <Globe className='w-3.5 h-3.5' />
                  Site web
                  <span className={optionalBadge}>optionnel</span>
                </span>
              </label>
              <input
                type='url'
                value={form.website}
                onChange={e => setField('website', e.target.value)}
                onBlur={e => validateField('website', e.target.value)}
                placeholder='https://www.mon-organisation.fr'
                className={errors.website ? inputErrorClass : inputClass}
              />
              {errors.website && <p className={errorClass}>{errors.website}</p>}
            </div>
          </div>
        </div>

        {/* Section 5 — Mon compte */}
        {user && (
          <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
            <h2 className='text-base font-bold text-slate-900 mb-6 flex items-center gap-2'>
              <User className='w-5 h-5 text-blue-600' />
              Mon compte
            </h2>

            <div className='space-y-5'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5'>
                <div>
                  <label className={labelClass}>Prénom</label>
                  <input type='text' readOnly value={user.firstName ?? '—'} className={readonlyClass} />
                </div>
                <div>
                  <label className={labelClass}>Nom</label>
                  <input type='text' readOnly value={user.lastName ?? '—'} className={readonlyClass} />
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  <span className='flex items-center gap-1.5'>
                    <Mail className='w-3.5 h-3.5' />
                    Email du compte
                  </span>
                </label>
                <input type='email' readOnly value={user.email} className={readonlyClass} />
                <p className='mt-1 text-xs text-slate-400'>Votre email de connexion ne peut pas être modifié ici.</p>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5'>
                <div>
                  <label className={labelClass}>Nom d&apos;utilisateur</label>
                  <input type='text' readOnly value={user.username} className={readonlyClass} />
                </div>
                <div>
                  <label className={labelClass}>Date d&apos;inscription</label>
                  <input
                    type='text'
                    readOnly
                    value={new Date(user.createdAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                    className={readonlyClass}
                  />
                </div>
              </div>

              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100'>
                <div className='min-w-0'>
                  <p className='font-semibold text-slate-900 text-sm'>Mot de passe</p>
                  <p className='text-xs text-slate-500 mt-0.5'>
                    Modifiez votre mot de passe pour sécuriser votre accès
                  </p>
                </div>
                <Link
                  to={ROUTES.FORGOT_PASSWORD}
                  className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-white hover:border-blue-300 transition text-sm font-semibold text-slate-700'
                >
                  <Key className='w-4 h-4' />
                  Changer le mot de passe
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Section — Parrainage (commenté)
        <div className='bg-white rounded-2xl border border-slate-200 p-4 sm:p-6'>
          <h2 className='text-base font-bold text-slate-900 mb-2 flex items-center gap-2'>
            <Gift className='w-5 h-5 text-indigo-600' />
            Mon code parrainage
          </h2>

          <p className='text-sm text-slate-500 mb-5 leading-relaxed max-w-2xl'>
            Partagez ce code avec d&apos;autres organisateurs ou des responsables de clubs. Chaque filleul qui
            souscrit un abonnement vous fait gagner des récompenses (mois Forfait gratuits, codes promo).
          </p>

          <div className='flex flex-col sm:flex-row sm:items-center gap-3 max-w-sm'>
            <input
              readOnly
              value={user?.referralCode ?? '—'}
              className='w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 font-mono text-xl font-extrabold tracking-widest text-slate-900 select-all cursor-text'
            />

            <button
              onClick={copyReferralCode}
              disabled={!user?.referralCode}
              title='Copier le code'
              className='w-full sm:w-12 cursor-pointer flex h-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40'
            >
              {copied ? <Check className='w-5 h-5 text-emerald-500' /> : <Copy className='w-5 h-5' />}
            </button>
          </div>

          {copied && <p className='mt-2 text-sm font-semibold text-emerald-600'>Code copié dans le presse-papiers !</p>}
        </div> */}

        {/* Bottom actions */}
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-end pt-2 pb-6 sm:pb-8 gap-3'>
          <Link
            to={ROUTES.ORGANIZER_DASHBOARD}
            className='w-full sm:w-auto px-6 py-2.5 border border-slate-200 rounded-xl hover:border-slate-300 transition text-sm font-semibold text-slate-700 text-center'
            onClick={e => {
              if (
                isDirty &&
                !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
              )
                e.preventDefault();
            }}
          >
            Annuler
          </Link>

          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !isDirty}
            className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold'
          >
            <Save className='w-4 h-4' />
            {saveMutation.isPending ? 'Enregistrement…' : 'Sauvegarder les modifications'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shell ────────────────────────────────────────────────────
export default function OrganizerProfileEdit() {
  const user = useAuthStore(s => s.user);
  const orgId = user?.organizationsAdmin[0]?.organizationId;

  const {
    data: org,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId ?? 0),
    queryFn: () => getOrganizationApi(orgId!),
    enabled: !!orgId,
  });

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      {isLoading && (
        <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6'>
          <div className='h-10 bg-slate-100 rounded-xl animate-pulse w-1/2 sm:w-1/3' />

          <div className='bg-white rounded-2xl border border-slate-200 p-6 animate-pulse space-y-4'>
            <div className='flex gap-6'>
              <div className='w-24 h-24 bg-slate-200 rounded-2xl' />
              <div className='flex-1 space-y-2'>
                <div className='h-4 bg-slate-200 rounded w-1/3' />
                <div className='h-8 bg-slate-200 rounded w-2/3' />
              </div>
            </div>
          </div>

          <div className='bg-white rounded-2xl border border-slate-200 p-6 animate-pulse space-y-4'>
            {[1, 2, 3].map(i => (
              <div key={i} className='h-10 bg-slate-200 rounded-lg' />
            ))}
          </div>
        </div>
      )}

      {isError && (
        <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
          <div className='bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 text-sm font-semibold'>
            Impossible de charger les informations de l&apos;organisation.
          </div>
        </div>
      )}

      {!isLoading && !isError && !orgId && (
        <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
          <div className='bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-700 text-sm font-semibold'>
            Votre compte n&apos;est associé à aucune organisation.
          </div>
        </div>
      )}

      {org && <EditForm org={org} />}
    </DashboardLayout>
  );
}
